import React, { useState, useContext, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DynamicLayout from "../../components/DynamicLayout";
import StructureLesson from "./tarot/structure";
import TheFoolLesson from "./tarot/thefoollesson";
import TheMagicianLesson from "./tarot/themagicianlesson";
import TheHighPriestessLesson from "./tarot/thehighpriestesslesson";
import TheEmpressLesson from "./tarot/theempresslesson";
import TheEmperorLesson from "./tarot/theemperorlesson";
import TheHierophantLesson from "./tarot/thehierophantlesson";
import TheLoversLesson from "./tarot/theloverslesson";
import TheChariotLesson from "./tarot/thechariotlesson";
import TheFoolVideoLesson from "./tarot/foolsjourneyvideo";
import { AcademyContext } from "../../context/academyContext";
import CourseHero from '../../components/CourseHero';

// Move functions before component definition
const getMaxQuestionsForLesson = (lessonNumber) => {
  // Return the number of questions for each lesson
  const questionCounts = {
    1: 4, // Structure lesson has 4 questions
    2: 3, // The Fool lesson has 3 questions
    3: 3, // The Magician lesson has 3 questions
    4: 3, // The High Priestess lesson has 3 questions
    5: 3, // The Empress lesson has 3 questions
    6: 3, // The Emperor lesson has 3 questions
    7: 3, // The Hierophant lesson has 3 questions
    8: 3, // The Lovers lesson has 3 questions
    9: 3, // The Chariot lesson has 3 questions
    12: 1, // The Fool's Journey Video has 1 question
  };
  return questionCounts[lessonNumber] || 1;
};

const getChapterAndLessonInfo = (lessonNumber) => {
  const chapterInfo = {
    1: { chapter: "Chapter 1: Introduction", lesson: "Structure and Basics" },
    2: { chapter: "Chapter 2: Major Arcana", lesson: "The Fool" },
    3: { chapter: "Chapter 2: Major Arcana", lesson: "The Magician" },
    4: { chapter: "Chapter 2: Major Arcana", lesson: "The High Priestess" },
    5: { chapter: "Chapter 2: Major Arcana", lesson: "The Empress" },
    6: { chapter: "Chapter 2: Major Arcana", lesson: "The Emperor" },
    7: { chapter: "Chapter 2: Major Arcana", lesson: "The Hierophant" },
    8: { chapter: "Chapter 2: Major Arcana", lesson: "The Lovers" },
    9: { chapter: "Chapter 2: Major Arcana", lesson: "The Chariot" },
    12: { chapter: "Chapter 3: The Fool's Journey", lesson: "Video Lesson" },
  };
  return chapterInfo[lessonNumber] || { chapter: "Unknown Chapter", lesson: "Unknown Lesson" };
};

const LessonPage = () => {
  const { progress, updateCourseProgress, updateQuestionProgress, getQuestionProgress } = useContext(AcademyContext);
  const [currentLesson, setCurrentLesson] = useState(progress.tarot?.completedLessons + 1 || 1);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [showHero, setShowHero] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const { level } = useParams(); // Get the level from URL parameters
  const navigate = useNavigate();

  // Define beginner course completion threshold (first 6 lessons)
  const BEGINNER_COURSE_LESSONS = 6;
  const isIntermediateUnlocked = (progress.tarot?.completedLessons || 0) >= BEGINNER_COURSE_LESSONS;

  // Memoize the max questions for current lesson
  const maxQuestions = useMemo(() => getMaxQuestionsForLesson(currentLesson), [currentLesson]);

  useEffect(() => {
    setCurrentLesson(progress.tarot?.completedLessons + 1 || 1);
    setCurrentQuestion(0);
  }, [progress.tarot?.completedLessons]);

  // Load question progress from Firebase when lesson changes
  useEffect(() => {
    const savedQuestionProgress = getQuestionProgress('tarot', currentLesson);
    if (savedQuestionProgress.length > 0) {
      // Pad the array to match maxQuestions length
      const paddedProgress = [...savedQuestionProgress];
      while (paddedProgress.length < maxQuestions) {
        paddedProgress.push(false);
      }
      setCompletedQuestions(paddedProgress);
    } else {
      setCompletedQuestions(Array(maxQuestions).fill(false));
    }
  }, [currentLesson, maxQuestions, getQuestionProgress]);

  // Memoize chapter and lesson info
  const { chapter, lesson } = useMemo(() => getChapterAndLessonInfo(currentLesson), [currentLesson]);

  // Function to update question progress in Firebase
  const updateQuestionProgressInFirebase = async (questionIndex, isCompleted) => {
    await updateQuestionProgress('tarot', currentLesson, questionIndex, isCompleted);
  };

  // Enhanced setCompletedQuestions that also saves to Firebase
  const setCompletedQuestionsWithFirebase = (newCompletedQuestions) => {
    // Handle different types of input
    let finalCompletedQuestions;
    
    if (typeof newCompletedQuestions === 'function') {
      // If it's a function, call it with the current state
      finalCompletedQuestions = newCompletedQuestions(completedQuestions);
    } else if (typeof newCompletedQuestions === 'number') {
      // If it's a number, create an array with that many completed questions
      finalCompletedQuestions = Array(completedQuestions.length).fill(false);
      for (let i = 0; i < newCompletedQuestions; i++) {
        finalCompletedQuestions[i] = true;
      }
    } else if (Array.isArray(newCompletedQuestions)) {
      // If it's already an array, use it directly
      finalCompletedQuestions = newCompletedQuestions;
    } else {
      // Fallback: use current state
      finalCompletedQuestions = completedQuestions;
    }
    
    setCompletedQuestions(finalCompletedQuestions);
    
    // Save the last changed question to Firebase
    const lastChangedIndex = finalCompletedQuestions.findIndex((completed, index) => 
      completed !== completedQuestions[index]
    );
    if (lastChangedIndex !== -1) {
      updateQuestionProgressInFirebase(lastChangedIndex, finalCompletedQuestions[lastChangedIndex]);
    }
  };

  const renderLesson = () => {
    switch (currentLesson) {
      case 1:
        return <StructureLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 2:
        return <TheFoolLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 3:
        return <TheMagicianLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 4:
        return <TheHighPriestessLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 5:
        return <TheEmpressLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 6:
        return <TheEmperorLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 7:
        return <TheHierophantLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 8:
        return <TheLoversLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 9:
        return <TheChariotLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 12:
        return <TheFoolVideoLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      default:
        return <StructureLesson onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
    }
  };

  const handleNext = () => {
    setCurrentLesson((prevLesson) => (prevLesson < 12 ? prevLesson + 1 : prevLesson));
  };

  const handlePrevious = () => {
    setCurrentLesson((prevLesson) => (prevLesson > 1 ? prevLesson - 1 : prevLesson));
  };

  const markLessonComplete = async () => {
    await updateCourseProgress('tarot', Math.max(progress.tarot?.completedLessons || 0, currentLesson));
  };

  const handleLessonChange = (event) => {
    setCurrentLesson(Number(event.target.value));
  };

  const handleLevelChange = (newLevel) => {
    navigate(`/academy/${newLevel}/lesson?lesson=${currentLesson}`);
  };

  const toggleHero = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setShowHero(!showHero);
    
    // Reset animation state after animation completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  return (
    <DynamicLayout>
      <CourseHero
        courseTitle="Tarot Course"
        level={level}
        chapter={chapter}
        lesson={lesson}
        currentLesson={currentLesson}
        completedLessons={progress.tarot?.completedLessons || 0}
        totalLessons={12}
        completedQuestions={completedQuestions}
        maxQuestions={getMaxQuestionsForLesson(currentLesson)}
        lessonOptions={
          <>
            <option value={1}>Structure Lesson</option>
            <option value={2}>The Fool Lesson</option>
            <option value={3}>The Magician Lesson</option>
            <option value={4}>The High Priestess Lesson</option>
            <option value={5}>The Empress Lesson</option>
            <option value={6}>The Emperor Lesson</option>
            <option value={7}>The Hierophant Lesson</option>
            <option value={8}>The Lovers Lesson</option>
            <option value={9}>The Chariot Lesson</option>
            <option value={12}>The Fool's Journey Video</option>
          </>
        }
        onLessonChange={handleLessonChange}
        onLevelChange={handleLevelChange}
        isIntermediateUnlocked={isIntermediateUnlocked}
        showHero={showHero}
        isAnimating={isAnimating}
        onToggleHero={toggleHero}
        currentQuestion={currentQuestion}
        onQuestionChange={setCurrentQuestion}
        totalQuestions={getMaxQuestionsForLesson(currentLesson)}
      />
      
      <div className="full-screen-image-container hasbackgroundblack margin-top-0 nomaxheight has-text-centered">
        <section className="container has-text-centered">
          {renderLesson()}
        </section>
      </div>
    </DynamicLayout>
  );
};

export default LessonPage;
